import type { AccountSummary } from '@fruitful/types';
import { AccountCategory, AccountType, Provider } from '@fruitful/types';

/** Display / picker values for Fruitful (Synctera / Orion / Apex) accounts. */
export enum MoneyMapperFruitfulAssetType {
    CHECKING_CASH = 'fruitful_checking_cash',
    SAVINGS = 'fruitful_savings',
    BROKERAGE = 'fruitful_brokerage',
    RETIREMENT = 'fruitful_retirement',
}

/** Display / picker values for linked non-Fruitful + manual accounts. */
export enum MoneyMapperExternalAssetType {
    CHECKING = 'ext_checking',
    SAVINGS = 'ext_savings',
    BROKERAGE = 'ext_brokerage',
    RETIREMENT = 'ext_retirement',
    FOUR_ZERO_ONE_K = 'ext_401k',
    CHILD_529 = 'ext_529',
}

export type MoneyMapperAssetTypeValue =
    | MoneyMapperFruitfulAssetType
    | MoneyMapperExternalAssetType;

export const FRUITFUL_HOLISTIC_PROVIDERS: Provider[] = [
    Provider.SYNCTERA,
    Provider.ORION,
    Provider.APEX,
];

export const isFruitfulHolisticAccount = (account: AccountSummary): boolean =>
    FRUITFUL_HOLISTIC_PROVIDERS.includes(account.provider);

export const membershipHasAnyFruitfulHolisticAccount = (
    accounts: AccountSummary[],
): boolean => accounts.some(isFruitfulHolisticAccount);

export const mapFruitfulAssetTypeToHolistic = (
    t: MoneyMapperFruitfulAssetType,
): AccountCategory => {
    switch (t) {
        case MoneyMapperFruitfulAssetType.CHECKING_CASH:
            return AccountCategory.SPEND;
        case MoneyMapperFruitfulAssetType.SAVINGS:
            return AccountCategory.SAVE;
        case MoneyMapperFruitfulAssetType.BROKERAGE:
        case MoneyMapperFruitfulAssetType.RETIREMENT:
            return AccountCategory.INVEST;
        default: {
            const _x: never = t;
            return _x;
        }
    }
};

export const mapExternalAssetTypeToHolistic = (
    t: MoneyMapperExternalAssetType,
): AccountCategory => {
    switch (t) {
        case MoneyMapperExternalAssetType.CHECKING:
            return AccountCategory.SPEND;
        case MoneyMapperExternalAssetType.SAVINGS:
            return AccountCategory.SAVE;
        case MoneyMapperExternalAssetType.BROKERAGE:
        case MoneyMapperExternalAssetType.RETIREMENT:
        case MoneyMapperExternalAssetType.FOUR_ZERO_ONE_K:
        case MoneyMapperExternalAssetType.CHILD_529:
            return AccountCategory.INVEST;
        default: {
            const _x: never = t;
            return _x;
        }
    }
};

const defaultFruitfulTypeFromHolistic = (
    category: AccountCategory,
): MoneyMapperFruitfulAssetType => {
    switch (category) {
        case AccountCategory.SPEND:
            return MoneyMapperFruitfulAssetType.CHECKING_CASH;
        case AccountCategory.SAVE:
            return MoneyMapperFruitfulAssetType.SAVINGS;
        case AccountCategory.INVEST:
            return MoneyMapperFruitfulAssetType.BROKERAGE;
        case AccountCategory.BORROW:
            return MoneyMapperFruitfulAssetType.CHECKING_CASH;
        default:
            return MoneyMapperFruitfulAssetType.CHECKING_CASH;
    }
};

const defaultExternalTypeFromHolistic = (
    category: AccountCategory,
): MoneyMapperExternalAssetType => {
    switch (category) {
        case AccountCategory.SPEND:
            return MoneyMapperExternalAssetType.CHECKING;
        case AccountCategory.SAVE:
            return MoneyMapperExternalAssetType.SAVINGS;
        case AccountCategory.INVEST:
            return MoneyMapperExternalAssetType.BROKERAGE;
        case AccountCategory.BORROW:
            return MoneyMapperExternalAssetType.CHECKING;
        default:
            return MoneyMapperExternalAssetType.CHECKING;
    }
};

const haystack = (account: AccountSummary): string =>
    `${account.name ?? ''} ${account.type ?? ''} ${account.rawType ?? ''} ${account.institutionName ?? ''}`.toLowerCase();

/** Heuristic: Orion / Apex / Spinwheel / Quiltt investment subtypes. */
const inferExternalAssetTypeFromStrings = (
    account: AccountSummary,
): MoneyMapperExternalAssetType | null => {
    const s = haystack(account);
    if (/\b529\b|ugma|utma|custodial|child|minor\b/.test(s)) {
        return MoneyMapperExternalAssetType.CHILD_529;
    }
    if (/\b401\b|403|457/.test(s)) {
        return MoneyMapperExternalAssetType.FOUR_ZERO_ONE_K;
    }
    if (
        /\bbrokerage\b|\bbroker\b|individual investment|joint investment/.test(
            s,
        )
    ) {
        return MoneyMapperExternalAssetType.BROKERAGE;
    }
    if (
        /\bira\b|roth|sep\b|simple\b|retirement|pension|annuity|inherited/.test(
            s,
        )
    ) {
        return MoneyMapperExternalAssetType.RETIREMENT;
    }
    if (/\bsav|money market|cd\b|goal/.test(s)) {
        return MoneyMapperExternalAssetType.SAVINGS;
    }
    if (/\bcheck|cash|spend|core\b|debit/.test(s)) {
        return MoneyMapperExternalAssetType.CHECKING;
    }
    return null;
};

const inferFruitfulAssetTypeFromStrings = (
    account: AccountSummary,
): MoneyMapperFruitfulAssetType | null => {
    const s = haystack(account);
    if (
        account.provider === Provider.SYNCTERA &&
        account.fruitfulAccountType != null
    ) {
        switch (account.fruitfulAccountType) {
            case AccountType.SAVING:
                return MoneyMapperFruitfulAssetType.SAVINGS;
            case AccountType.CHECKING:
                return MoneyMapperFruitfulAssetType.CHECKING_CASH;
            case AccountType.CHARGE_SECURED:
            case AccountType.CHARGE_UNSECURED:
            case AccountType.LINE_OF_CREDIT:
                return MoneyMapperFruitfulAssetType.CHECKING_CASH;
            default:
                break;
        }
    }
    if (
        /joint brokerage|individual brokerage|trust brokerage|\bbrokerage\b/.test(
            s,
        )
    ) {
        return MoneyMapperFruitfulAssetType.BROKERAGE;
    }
    if (
        /\bira\b|roth|401|403|sep|simple|inherited|retirement|ugma|utma|529|custodial/.test(
            s,
        )
    ) {
        return MoneyMapperFruitfulAssetType.RETIREMENT;
    }
    if (/\bsav|goal|money market/.test(s)) {
        return MoneyMapperFruitfulAssetType.SAVINGS;
    }
    if (/\bcheck|cash|core|spend/.test(s)) {
        return MoneyMapperFruitfulAssetType.CHECKING_CASH;
    }
    return null;
};

export const inferFruitfulAssetType = (
    account: AccountSummary,
): MoneyMapperFruitfulAssetType => {
    const fromStrings = inferFruitfulAssetTypeFromStrings(account);
    if (fromStrings != null) {
        return fromStrings;
    }
    return defaultFruitfulTypeFromHolistic(account.category);
};

export const inferExternalAssetType = (
    account: AccountSummary,
): MoneyMapperExternalAssetType => {
    const fromStrings = inferExternalAssetTypeFromStrings(account);
    if (fromStrings != null) {
        return fromStrings;
    }
    return defaultExternalTypeFromHolistic(account.category);
};

export const inferAssetTypeForAccount = (
    account: AccountSummary,
): MoneyMapperAssetTypeValue =>
    isFruitfulHolisticAccount(account)
        ? inferFruitfulAssetType(account)
        : inferExternalAssetType(account);

export const moneyMapperFruitfulAssetTypePickerItems = (): Array<{
    label: string;
    value: MoneyMapperFruitfulAssetType;
}> => [
    {
        label: 'Checking',
        value: MoneyMapperFruitfulAssetType.CHECKING_CASH,
    },
    { label: 'Savings', value: MoneyMapperFruitfulAssetType.SAVINGS },
    { label: 'Brokerage', value: MoneyMapperFruitfulAssetType.BROKERAGE },
    { label: 'Retirement', value: MoneyMapperFruitfulAssetType.RETIREMENT },
];

export const moneyMapperExternalAssetTypePickerItems = (): Array<{
    label: string;
    value: MoneyMapperExternalAssetType;
}> => [
    { label: 'Checking', value: MoneyMapperExternalAssetType.CHECKING },
    { label: 'Savings', value: MoneyMapperExternalAssetType.SAVINGS },
    { label: 'Brokerage', value: MoneyMapperExternalAssetType.BROKERAGE },
    { label: 'Retirement', value: MoneyMapperExternalAssetType.RETIREMENT },
    { label: '401k', value: MoneyMapperExternalAssetType.FOUR_ZERO_ONE_K },
    {
        label: 'Child account / 529',
        value: MoneyMapperExternalAssetType.CHILD_529,
    },
];

const isFruitfulAssetTypeValue = (
    v: MoneyMapperAssetTypeValue,
): v is MoneyMapperFruitfulAssetType =>
    (Object.values(MoneyMapperFruitfulAssetType) as string[]).includes(v);

export const assetTypeToHolisticCategory = (
    value: MoneyMapperAssetTypeValue,
): AccountCategory =>
    isFruitfulAssetTypeValue(value)
        ? mapFruitfulAssetTypeToHolistic(value)
        : mapExternalAssetTypeToHolistic(value);

export const isBorrowCategoryAccount = (account: AccountSummary): boolean =>
    account.category === AccountCategory.BORROW;

/** Account nickname can be updated from AccountEditModal for these linked providers. */
export const canEditLinkedAccountNicknameInTable = (
    account: AccountSummary,
): boolean => {
    if (account.provider === Provider.QUILTT) {
        return account.userId != null;
    }
    if (account.provider === Provider.ORION) {
        return account.userId != null;
    }
    if (account.provider === Provider.SYNCTERA) {
        return true;
    }
    if (account.provider === Provider.SPINWHEEL) {
        return true;
    }
    return false;
};

/** Linked accounts where holistic category can be updated (matches {@link persistHolisticCategoryForAccount} providers). */
export const canEditLinkedHolisticCategoryInTable = (
    account: AccountSummary,
): boolean =>
    !isBorrowCategoryAccount(account) &&
    [
        Provider.QUILTT,
        Provider.SYNCTERA,
        Provider.ORION,
        Provider.SPINWHEEL,
    ].includes(account.provider);

const fruitfulLabelMap: Record<MoneyMapperFruitfulAssetType, string> = {
    [MoneyMapperFruitfulAssetType.CHECKING_CASH]: 'Checking',
    [MoneyMapperFruitfulAssetType.SAVINGS]: 'Savings',
    [MoneyMapperFruitfulAssetType.BROKERAGE]: 'Brokerage',
    [MoneyMapperFruitfulAssetType.RETIREMENT]: 'Retirement',
};

const externalLabelMap: Record<MoneyMapperExternalAssetType, string> = {
    [MoneyMapperExternalAssetType.CHECKING]: 'Checking',
    [MoneyMapperExternalAssetType.SAVINGS]: 'Savings',
    [MoneyMapperExternalAssetType.BROKERAGE]: 'Brokerage',
    [MoneyMapperExternalAssetType.RETIREMENT]: 'Retirement',
    [MoneyMapperExternalAssetType.FOUR_ZERO_ONE_K]: '401k',
    [MoneyMapperExternalAssetType.CHILD_529]: 'Child account / 529',
};

export const getMoneyMapperAssetTypeLabel = (
    value: MoneyMapperAssetTypeValue,
): string =>
    isFruitfulAssetTypeValue(value)
        ? fruitfulLabelMap[value]
        : externalLabelMap[value];

export const typeColumnLabelForAccount = (account: AccountSummary): string => {
    if (isBorrowCategoryAccount(account)) {
        const raw = account.type ?? account.rawType;
        return raw != null && raw.trim() !== '' ? raw : '—';
    }
    const v = inferAssetTypeForAccount(account);
    return getMoneyMapperAssetTypeLabel(v);
};
